import * as THREE from 'three'
import { BALL, COURT_CENTER } from '../constants.js'
import { ballSpin, tumbleSpin, MAX_SPIN_RATE } from './ballSpin.js'

// ============================================================================
//  shotCycle — automat stanów 10 s sterujący piłką i siatką.
//
//    DRIBBLE (5.8s)  kozłowanie na środku półboiska
//    SHOOT   (1.6s)  lot po paraboli do obręczy
//    SWISH   (1.0s)  opadanie przez obręcz -> impuls na siatkę
//    RESET   (1.6s)  powrót na środek (zanikające odbicia)
// ============================================================================

const D = 5.8
const S = 1.6
const W = 1.0
const R = 1.6
const TOTAL = D + S + W + R // = 10.0

const easeInQuad = (x) => x * x
const lerp = THREE.MathUtils.lerp

export class ShotCycle {
  constructor(ball, net, rimCenter) {
    this.ball = ball
    this.net = net
    this.rim = rimCenter.clone()
    this.t = 0
    this._punched = false

    this.center = new THREE.Vector3(COURT_CENTER.x, BALL.RADIUS, COURT_CENTER.z)
    this.releaseY = 1.6

    this._prev = new THREE.Vector3().copy(this.center)
    this._axis = new THREE.Vector3()
    this._spinT = 0 // zegar tumblingu — bez modulo, by oś precesji nie skakała na zawinięciu cyklu
  }

  // Nowa pozycja obręczy (kosz z innym wysięgiem) — trajektoria liczy się od
  // this.rim przy każdej klatce, więc wystarczy podmiana celu.
  setRim(rimCenter) {
    this.rim.copy(rimCenter)
  }

  // Ustawia środek kozłowania/rzutu (połowa długości boiska). Wołane po zmianie
  // wymiarów — resetuje _prev, by uniknąć skoku obrotu piłki w następnej klatce.
  setCenter(z) {
    this.center.set(0, BALL.RADIUS, z)
    this._prev.copy(this.center)
  }

  _dribble(t) {
    const period = 0.58
    const h = 0.95
    const y = BALL.RADIUS + h * Math.abs(Math.sin((t / period) * Math.PI))
    return new THREE.Vector3(this.center.x, y, this.center.z)
  }

  _shoot(t) {
    const u = t / S
    const z = lerp(this.center.z, this.rim.z, u)
    const baseY = lerp(this.releaseY, this.rim.y, u)
    const arc = 2.7
    const y = baseY + arc * Math.sin(Math.PI * u)
    return new THREE.Vector3(0, y, z)
  }

  _swish(t) {
    const v = t / W
    const y = lerp(this.rim.y, BALL.RADIUS, easeInQuad(v))
    return new THREE.Vector3(0, y, this.rim.z)
  }

  _reset(t) {
    const w = t / R
    const z = lerp(this.rim.z, this.center.z, w)
    const bounce = 0.55 * Math.abs(Math.sin(w * Math.PI * 2)) * (1 - w)
    return new THREE.Vector3(0, BALL.RADIUS + bounce, z)
  }

  update(dt) {
    this.t = (this.t + dt) % TOTAL
    const t = this.t

    let pos
    if (t < D) {
      this._punched = false
      pos = this._dribble(t)
    } else if (t < D + S) {
      pos = this._shoot(t - D)
    } else if (t < D + S + W) {
      const lt = t - (D + S)
      if (!this._punched) {
        // moment trafienia: impuls na siatkę
        this.net.punch(new THREE.Vector3(0, this.rim.y - 0.1, this.rim.z), 1.0)
        this._punched = true
      }
      pos = this._swish(lt)
    } else {
      pos = this._reset(t - (D + S + W))
    }

    // Obrót piłki: toczenie z ruchu poziomego, a w fazach czysto pionowych
    // (kozioł, swish) backspin wokół osi X — patrz ballSpin.js.
    const s = ballSpin(
      [this._prev.x, this._prev.y, this._prev.z],
      [pos.x, pos.y, pos.z],
      BALL.RADIUS,
    )
    if (s.angle > 1e-6) {
      this._axis.set(s.axis[0], s.axis[1], s.axis[2])
      // Clamp: wzór toczenia w locie daje nierealnie szybki obrót (>25 rad/s).
      this.ball.spin(this._axis, Math.min(s.angle, MAX_SPIN_RATE * dt))
    }

    // Tumbling: powolny obrót o precesującej osi nakładany na toczenie —
    // dzięki niemu piłka wiruje we wszystkich osiach, a nie „na rożnie".
    this._spinT += dt
    const tb = tumbleSpin(this._spinT, dt)
    if (tb.angle > 0) {
      this._axis.set(tb.axis[0], tb.axis[1], tb.axis[2])
      this.ball.spin(this._axis, tb.angle)
    }

    this.ball.setPosition(pos.x, pos.y, pos.z)
    this._prev.copy(pos)
  }
}
